import React, { useEffect, useMemo, useState } from "react";
import "../../assets/custom/css/cti.css";
import NavBar from "./NavBar";
import ConferenceScreen from "./screens/ConferenceScreen";
import IdleScreen from "./screens/IdleScreen";
import OnCallScreen from "./screens/OnCallScreen";
import RingingScreen from "./screens/RingingScreen";
import { Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { Button } from "primereact/button";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { postApiCall, getApiCall } from "../../service/httpCall";
import {
  API_BREAK_LIST,
  USER_BREAKS_EDIT,
  USER_BREAKS_CREATE,
} from "../../service/url";
import { toast } from "react-toastify";

const durationToSeconds = (duration) => {
  const minutes = parseInt(duration, 10);
  return minutes * 60;
};

function CTI() {
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const handleShow = () => setShowBreakModal(true);
  const handleClose = () => setShowBreakModal(false);
  const [selectedBreakId, setSelectedBreakId] = useState("");
  const [screen, setScreen] = useState("idle");
  const [userStatus, setUserStatus] = useState("AVAILABLE");
  const [theme, setTheme] = useState(
    () => localStorage.getItem("cti-theme") || "light",
  );
  const [breakExceeded, setBreakExceeded] = useState(false);
  const [activeTab, setActiveTab] = useState("log");
  const [dialNum, setDialNum] = useState("");
  const [holdOn, setHoldOn] = useState(false);
  const [muteOn, setMuteOn] = useState(false);
  const [idleStatus, setIdleStatus] = useState("READY");
  const [idleSeconds, setIdleSeconds] = useState(444);
  const [callSeconds, setCallSeconds] = useState(272);
  const [confSeconds, setConfSeconds] = useState(728);
  const [breaksData, setBreaksData] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    localStorage.setItem("cti-theme", theme);
  }, [theme]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (screen === "idle") setIdleSeconds((value) => value + 1);
      if (screen === "oncall") setCallSeconds((value) => value + 1);
      if (screen === "conference") setConfSeconds((value) => value + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [screen]);

  const statusMeta = useMemo(() => {
    if (screen === "ringing") return { text: "RINGING", dotClass: "ring" };
    if (screen === "oncall") return { text: "ON CALL", dotClass: "busy" };
    if (screen === "conference") return { text: "CONF", dotClass: "busy" };
    return { text: "READY", dotClass: "" };
  }, [screen]);

  const handleConfirm = async () => {
    if (!selectedBreakId) {
      toast.error("Please select a break");
      return;
    }

    try {
      const resp = await postApiCall(USER_BREAKS_CREATE, {
        reason: selectedBreakId,
      });
      const selectedBreak = breaksData.find((b) => b.id == selectedBreakId);
      const seconds = durationToSeconds(Number(selectedBreak.duration));

      // Get existing data from AsyncStorage
      const existingData = await AsyncStorage.getItem("activeBreak");
      let breakArray = [];

      // Parse existing data safely
      if (existingData) {
        try {
          const parsedData = JSON.parse(existingData);
          if (Array.isArray(parsedData)) {
            breakArray = parsedData;
          } else if (parsedData) {
            breakArray = [parsedData];
          }
        } catch (e) {
          console.error("Error parsing activeBreak:", e);
          breakArray = [];
        }
      }

      const endTime = Date.now() + seconds * 1000;
      // Add new break object
      breakArray.push({
        breakData: resp?.userBreaks,
        endTime: Date.now() + seconds * 1000,
        user_id: resp?.userBreaks?.user_id,
      });

      setBreakEndTime(endTime);

      // Save back to AsyncStorage
      await AsyncStorage.setItem("activeBreak", JSON.stringify(breakArray));

      setCountdown(seconds);
      setUserStatus("BREAK");
      handleClose();
      toast.success("Break Status Applied");
    } catch (error) {
      console.log("error while updating user break status", error);

      toast.error(error || `Error while Applying Break`);
      // setShowToast(true);
    }
  };

  const cancelBreak = async () => {
    if (!userId) {
      toast.error("User Id Not Found");
      return;
    }
    try {
      const url = USER_BREAKS_EDIT.replace(":id", userId);
      await postApiCall(url);
      // remove fron asyncStotage

      const existingData = await AsyncStorage.getItem("activeBreak");
      if (existingData) {
        let breakArray = JSON.parse(existingData);

        breakArray = breakArray.filter((b) => b.user_id !== userId);

        if (breakArray.length > 0) {
          await AsyncStorage.setItem("activeBreak", JSON.stringify(breakArray));
        } else {
          await AsyncStorage.removeItem("activeBreak");
        }
      }

      setCountdown(null);
      setShowCancelModal(false);
      setSelectedBreakId(null);
      setUserStatus("AVAILABLE");
      setBreakExceeded(false);
      toast.success("Break cancelled");
    } catch (error) {
      console.log("error while updating user break status", error);
      toast.error(error || `Error while Cancelling Break`);
      // setShowToast(true);
    }
  };

  const fetchUserData = async () => {
    try {
      const user = JSON.parse(await AsyncStorage.getItem("user"));
      if (user) {
        setUserData(user);
      }
    } catch (error) {
      console.log("Error getting logged in user data", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const checkAndRestoreBreak = async () => {
    const breakDataStorage = await AsyncStorage.getItem("activeBreak");
    const user = JSON.parse(await AsyncStorage.getItem("user"));

    if (!breakDataStorage) return;

    try {
      const breakArray = JSON.parse(breakDataStorage);

      const userBreak = breakArray.find((b) => b.user_id === user?.id);

      if (!userBreak) return;

      const { breakData, endTime } = userBreak;
      const remainingSeconds = Math.floor((endTime - Date.now()) / 1000);

      setUserStatus("BREAK");
      setSelectedBreakId(breakData.id);
      setBreakEndTime(endTime);

      if (remainingSeconds > 0) {
        setCountdown(remainingSeconds);
        setBreakEndTime(endTime);
        setBreakExceeded(false);
      } else {
        // Break time exceeded
        setCountdown(0);
        setBreakExceeded(true);
      }
    } catch (e) {
      console.error("Error restoring break state:", e);
      setUserStatus("AVAILABLE");
    }
  };

  useEffect(() => {
    getApiCall(`${API_BREAK_LIST}?status=1`, {})
      .then(onListSuccess)
      .catch(onError);
  }, []);

  function onError(err) {
    console.log(err);
  }

  function onListSuccess(resp) {
    setBreaksData(resp);
  }

  return (
    <>
      <div className="cti-app-shell" data-theme={theme}>
        <NavBar
          screen={screen}
          onScreenChange={setScreen}
          statusMeta={statusMeta}
          theme={theme}
          userData={userData}
          onToggleTheme={() =>
            setTheme((value) => (value === "light" ? "dark" : "light"))
          }
        />

        {screen === "idle" && (
          <IdleScreen
            idleSeconds={idleSeconds}
            idleStatus={idleStatus}
            activeTab={activeTab}
            dialNum={dialNum}
            breakEndTime={breakEndTime}
            countdown={countdown}
            breaksData={breaksData}
            selectedBreakId={selectedBreakId}
            onDial={(value) => setDialNum((current) => current + value)}
            onDialBack={() => setDialNum((current) => current.slice(0, -1))}
            onTabChange={setActiveTab}
            onStatusChange={setIdleStatus}
            onPlaceCall={() => setScreen("oncall")}
            onSetBreakClick={() => setShowBreakModal(true)}
            onRemoveBreakClick={() => setShowCancelModal(true)}
          />
        )}

        {screen === "ringing" && (
          <RingingScreen onAccept={() => setScreen("oncall")} />
        )}

        {screen === "oncall" && (
          <OnCallScreen
            callSeconds={callSeconds}
            holdOn={holdOn}
            muteOn={muteOn}
            onToggleHold={() => setHoldOn((value) => !value)}
            onToggleMute={() => setMuteOn((value) => !value)}
            onGoConference={() => setScreen("conference")}
            onEndCall={() => setScreen("idle")}
          />
        )}

        {screen === "conference" && (
          <ConferenceScreen
            confSeconds={confSeconds}
            onLeave={() => setScreen("oncall")}
            onEndAll={() => setScreen("idle")}
          />
        )}
      </div>
      {/* Break Selection Modal */}
      <Modal show={showBreakModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="breakSelect">
            <Form.Label>
              Select a break:
              <span style={{ color: "red", margin: "0 4px" }}>*</span>
            </Form.Label>
            <Form.Select
              value={selectedBreakId}
              onChange={(e) => setSelectedBreakId(e.target.value)}
            >
              <option value="">Select Break</option>
              {breaksData.map((brk) => (
                <option key={brk.id} value={brk.id}>
                  {brk.title} - ({brk.duration} Mins)
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#1789e5ff" }}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Break Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel the break?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            No
          </Button>
          <Button
            variant="danger"
            onClick={cancelBreak}
            style={{ backgroundColor: "#1789e5ff" }}
          >
            Yes, Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CTI;
